import React, { useEffect, useState } from 'react';
import {
  Card,
  Box,
  Typography,
  Button,
  Divider,
  useMediaQuery,
  Tooltip,
  IconButton,
  Hidden,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { FormattedMessage, useIntl } from 'react-intl';
import moment from 'moment';
import { get } from 'lodash';
import {
  currencyFormat,
  dateTimeFormat,
  getCurrency,
  getOrderCount,
  goToOrderDetails,
  orderStatus,
  updateDecimals,
} from 'src/utils';
import { useHistory } from 'react-router-dom';
import PartialRefundedIcon from 'src/assets/svgIcons/PartialRefundedIcon';
import { useSelector } from 'react-redux';

const useStyles = makeStyles((theme: any) => ({
  root: {
    height: 'auto !important',
    width: '100%',
    [theme.breakpoints.up('lg')]: {
      maxWidth: '680px',
    },
    padding: 10,
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up('xs')]: {
      margin: 10,
    },
  },
  header: {
    display: 'flex',
    flex: 0.5,
    marginLeft: 24,
    marginRight: 15,
    [theme.breakpoints.down('sm')]: {
      marginLeft: 10,
      marginRight: 10,
    },
  },
  body: {
    display: 'flex',
    flex: 0.5,
    alignItems: 'center',
    marginLeft: 24,
    justifyContent: 'space-between',
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('sm')]: {
      marginLeft: 10,
      padding: '10px 0'
    },
  },
  order: {
    fontWeight: 600,
    fontSize: '20px',
    color: '#111928',
    alignSelf: 'flex-start',
    marginBottom: 6,
  },
  date: {
    fontSize: '14px',
    fontWeight: 400,
    color: '#637381',
    alignSelf: 'flex-start',
  },
  storeName: {
    fontSize: '14px',
    color: '#111928',
    fontWeight: 500,
    alignSelf: 'flex-start',
    marginBottom: 4,
    textAlign: 'left',
  },
  orderDateTimeContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 0.5,
    [theme.breakpoints.down('sm')]: {
      textAlign: 'left',
      flex: 1,
    },
  },
  completeButton: {
    lineHeight: 'normal',
    height: '44px !important',
    fontWeight: 'bold',
    backgroundColor: theme.palette.primary.main,
    textTransform: 'none',
    boxShadow: 'none',
    color: theme.palette.color_white.main,
    fontSize: 15,
    marginLeft: 10,
    [theme.breakpoints.down('sm')]: {
      minWidth: '45%',
      width: 'auto',
    },
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      boxShadow: 'none',
    },
  },
  grandioseButton: {
    lineHeight: 'normal',
    height: '44px !important',
    fontWeight: 'bold',
    backgroundColor: '#fff',
    border: (props: any) => `solid 1px ${props.color ? theme.palette[props.color].main : 'transparent'}`,
    textTransform: 'none',
    boxShadow: 'none',
    color: (props: any) =>
      props.color ? props.variant === 'outlined'
        ? theme.palette[props.color].main
        : theme.palette.primary.main : 'transparent',
    fontSize: 15,
    [theme.breakpoints.down('sm')]: {
      minWidth: '50%',
      width: 'auto',
    },
  },
  button: {
    lineHeight: 'normal',
    minHeight: 50,
    height: 'auto !important',
    fontWeight: 'bold',
    backgroundColor: '#fff',
    border: (props: any) => `solid 1px ${props.color ? theme.palette[props.color].main : 'transparent'}`,
    textTransform: 'none',
    boxShadow: 'none',
    color: (props: any) =>
      props.color ? props.variant === 'outlined'
        ? theme.palette[props.color].main
        : theme.palette.primary.main : 'transparent',
    fontSize: 15,
    [theme.breakpoints.down('sm')]: {
      minWidth: '50%',
      width: 'auto',
    },
  },
  buttonContainer: {
    display: 'flex',
    flex: 0.5,
    justifyContent: 'flex-end',
    [theme.breakpoints.down('xs')]: {
      flex: 1,
      textAlign: 'left',
      margin: '5px 10px 0px 10px',
      paddingBottom: 10,
      borderBottom: '1px solid #DFE4EA',
      justifyContent: 'flex-start',
    },
  },
  bodyItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginRight: 30,
    [theme.breakpoints.down('xs')]: {
      width: '30%',
    },
    width: '20%',
  },
  grandioseItem: {
    color: theme.palette.color_dark_text.main,
    fontSize: 14,
    fontWeight: 400,
  },
  viewAllText: {
    color: theme.palette.primary.main,
    fontSize: 14,
    fontWeight: 500,
    textDecoration: 'underline',
    marginRight: 10,
  },
  item1: {
    fontSize: 14,
    color: '#637381',
    fontWeight: 400,
  },
  item2: {
    fontSize: 14,
    color: '#111928',
    fontWeight: 500,
    textAlign: 'left',
  },
}));

const CCOrdersCard = ({ item, onChangeCCOrderStatus }: any) => {
  let hasGrandioseOnlyStores = true;
  const { active_stores } = useSelector((state: any) => state.store);
  const {
    o_id,
    date,
    order_status,
    cc_order_status,
    discounted_price,
    currency,
    store_id,
    store_name,
    order_id,
    transaction_id_poslog,
    refunded_items = [],
    non_refunded_items = [],
  } = item;
  const customer = `${get(item, 'customer.first_name', '')} ${get(
    item,
    'customer.last_name',
    '',
  )}`;
  const storetype = get(item, 'store_type', '');
  const itemList = [
    {
      value: `${new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: currencyFormat(currency),
      }).format(discounted_price)}`,
      // value: `${getCurrency(currency)} ${updateDecimals(
      //   discounted_price,
      // ).toFixed(2)}`,
      title: <FormattedMessage id="total" defaultMessage="Total" />,
    },
    {
      value: getOrderCount(item),
      title: <FormattedMessage id="items" defaultMessage="Items" />,
    },
    {
      value: customer,
      title: <FormattedMessage id="customer" defaultMessage="Customer" />,
    },
    {
      value: transaction_id_poslog,
      title: (
        <FormattedMessage id="transactionIdPoslog" defaultMessage="Transaction ID Poslog" />
      ),
    },
  ];
  let isPartialRefund =
    get(item, 'non_refunded_items', []).length > 0 &&
    get(item, 'refunded_items', []).length > 0
      ? true
      : false;
  let orderStatusVal =
    order_status === 'refunded' && cc_order_status === 'collected'
      ? 'collected_refunded'
      : cc_order_status;

  const { icon, status, color, variant } = orderStatus(
    orderStatusVal,
    isPartialRefund,
    useMediaQuery('(max-width:600px)'),
  );
  const history = useHistory();

  const classes = useStyles({ color, variant });
  const intl = useIntl();
  const grandioseItems = [...get(item, 'non_refunded_items', [])].filter(
    (nonRefundedItem: any) => nonRefundedItem.item_type === 'REGULAR' || nonRefundedItem.item_type === 'WITH_ADDONS',
  );
  const [toast, showToast] = useState<any>({
    visible: false,
    message: '',
    severity: 'error',
  });
  const checkStoreTypes = () => {
    active_stores?.filter((store: any) => {
      if (store?.store_type !== 'GrandioseStoreType') {
        hasGrandioseOnlyStores = false;
      }
      
    });
    return {
      GrandioseStoreType: hasGrandioseOnlyStores,
    };
  };

  return (
    <Card
      className={classes.root}
      variant="outlined"
      onClick={() => {
        goToOrderDetails({
          order_id,
          type: 'click_and_collect',
          from: 'orders',
          history,
        });
      }}
    >
      <Box className={classes.header} style={{ alignItems: checkStoreTypes()['GrandioseStoreType'] ? 'flex-start' :'center', maxBlockSize: checkStoreTypes()['GrandioseStoreType'] ? 'fit-content' : '' }}>
        <Box className={classes.orderDateTimeContainer}>
          <Typography className={classes.order}>{`#${o_id}`}</Typography>
          <Typography className={classes.date}>
            {moment(date).format(dateTimeFormat(storetype))} ( UTC{' '}
            {moment(date).utc().format('HH:mm')} )
          </Typography>
          <Typography className={classes.storeName}>{store_name}</Typography>
        </Box>
        {!checkStoreTypes()['GrandioseStoreType'] && (
          <Hidden only={['xs']}>
            <Box className={classes.buttonContainer} style={{ margin: hasGrandioseOnlyStores ? 'auto': '', maxBlockSize: 'fit-content'}}>
              <Button startIcon={icon} className={classes.button} variant={variant}>
                {status}
              </Button>
            </Box>
          </Hidden>
        )}
        {checkStoreTypes()['GrandioseStoreType'] && (
          <Hidden only={['xs', 'sm']}>
            <Box className={classes.buttonContainer} style={{ flexDirection: 'column', alignItems: 'end', maxBlockSize: 'fit-content'}}>
              <Button startIcon={icon} className={classes.grandioseButton} variant={variant}  style={{ width: 150 }}>
                {status}
              </Button>
              {(cc_order_status !== 'collected' && cc_order_status !== 'rejected_store' && cc_order_status !== 'rejected_customer') && (<Button className={classes.completeButton} style={{ marginLeft: 0, marginTop: 10, width: 150 }} onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onChangeCCOrderStatus(
                  order_id,
                  'collected'
                )
              }
              }>
                Complete
              </Button>)}
            </Box>
          </Hidden>
        )}
      </Box>
      {!checkStoreTypes()['GrandioseStoreType'] && (
        <Hidden only={['sm', 'md', 'lg', 'xl']}>
          <Box className={classes.buttonContainer}>
            <Button startIcon={icon} className={classes.button} variant={variant}>
              {status}
            </Button>
          </Box>
        </Hidden>
      )}
      {checkStoreTypes()['GrandioseStoreType'] && (
        <Hidden only={['md', 'lg', 'xl']}>
          <Box className={classes.buttonContainer} style={{ maxBlockSize: 'fit-content'}}>
            <Button startIcon={icon} className={classes.grandioseButton} style={{width: '45%', height: '50px !important'}} variant={variant}>
              {status}
            </Button>
            {(cc_order_status !== 'collected' && cc_order_status !== 'rejected_store' && cc_order_status !== 'rejected_customer') && (<Button className={classes.completeButton} onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onChangeCCOrderStatus(
                order_id,
                'collected'
              )
            }
            }>
              Complete
            </Button>)}
          </Box>
        </Hidden>
      )}
      <Divider light style={{marginTop: hasGrandioseOnlyStores ? 10 : 0}} />
      <Box className={classes.body} style={{ alignItems: hasGrandioseOnlyStores ? 'flex-start' : ''}}>
        <Box style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', width: '100%', justifyContent: hasGrandioseOnlyStores ? 'space-between' : 'none' }}>
          {!checkStoreTypes()['GrandioseStoreType'] && itemList.map((item: any, index: number) => (
            <Box
              className={classes.bodyItem}
              key={index}
            >
              <Typography className={classes.item1}>{item.title}</Typography>
              <Typography className={classes.item2}>{item?.value}</Typography>
            </Box>
          ))}
          {checkStoreTypes()['GrandioseStoreType'] && grandioseItems.map((item: any, index: number) => (
            <Box
              className={classes.bodyItem} style={{display: 'block', width: index == 6 ? 'inherit' : 'auto', marginRight: 0}}
              key={index}
            >
              {index < 6 && (<Typography className={classes.grandioseItem} style={{marginRight: 20}}>{item.item_name}</Typography>)}
              {index == 6 && (<Box style={{display: 'flex', justifyContent: 'space-between'}}><Box style={{paddingRight: 15}}>+{grandioseItems.length - 6} more</Box><Typography className={classes.viewAllText}>View All</Typography></Box>)}
            </Box>
          ))}
        </Box>
        {refunded_items.length > 0 && non_refunded_items.length !== 0 && (
          <Box style={{ marginRight: 16, alignItems: 'center' }}>
            <Tooltip title={intl.formatMessage({ id: 'partialRefundAlert' })}>
              <IconButton>
                <PartialRefundedIcon color="error" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
    </Card>
  );
};

export default CCOrdersCard;
